import React, { Fragment } from 'react';
import styled from 'styled-components';
import { compose } from 'redux';
import withProps from 'recompose/withProps';
import branch from 'recompose/branch';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Typo } from 'tmt-ui-kit';
import Tooltip from '~/components/Tooltip';
import LineLoader from '../../Loader/LineLoader';
import withTrip from './withTrip';

import { convertPriceDetail as convert } from '~/utils/price';
import { thaiCities } from '~/utils/trip';

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  :nth-last-child(2) {
    margin-bottom: 0;
  }
  ${props => props.bookNow && `
    >p:first-child {
      padding-left: 16px;
    }
    >p:last-child {
      color: ${props.theme.red} !important;
    }
  `}
`;

const GreenText = styled(Typo.Medium)`
  margin: 0 !important;
  color: ${props => props.theme.greenLighter} !important;
`;

const MediumText = styled(Typo.Medium)`
  margin: 0 !important;
`;

const Text = styled(Typo.Paragraph)`
  margin: 0 !important;
  padding-bottom: 0;
`;

const Total = styled.span`
  font-size: 16px !important;
  font-weight: 500 !important;
  color: ${props => (props.isMasterTrip ? props.theme.greenLighter : props.theme.orange)} !important;
`;

const Satisfaction = styled(Typo.Paragraph)`
  margin: 0 !important;
  font-size: 13px !important;
  text-align: right;
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTooltip = styled(Tooltip)`
  height: 24px;
  .tooltip-popup {
    width: 320px;
    left: -130px;
    span {
      font-size: 14px;
    }
  }
`;

const renderRow = isLoading => (left, right, noMargin = false, bookNow = false) => (
  <Row bookNow={bookNow} noMargin={noMargin}>
    <Text>{left}</Text>
    {isLoading ? <LineLoader /> : <Text>{right}</Text>}
  </Row>
);

const myIsNaN = (number) => Number.isNaN(number) || number === 'NaN';

const priceConverter = (currency, rates) => price => convert(price, currency, rates);

const enhance = compose(
  connect(
    state => ({
      pricing: state.trip.pricing,
      currency: state.session.data.currency,
      rates: state.info.exchangeRates,
      isLoadingPricing: state.trip.isLoadingPricing,
    })
  ),
  branch(
    props => props.trip.product_type === 'trip',
    withProps(
      props => {
        const { pricing, quantity, child } = props;
        const adultPerPerson = pricing.seasonedDiscountedPerPerson;
        const totalAdults = adultPerPerson * quantity;
        const totalChild = (child && child.price) ? child.price * child.quantity : 0;
        const bookingFee = totalAdults * 0.2;
        const total = pricing.grandTotal;
        const bookNowAndSave = total - (totalAdults + totalChild + bookingFee) || 0;
        const realBookingFee = pricing.bookingFee + pricing.tax;
        return {
          adultPerPerson,
          totalAdults,
          totalChild,
          bookingFee,
          bookNowAndSave,
          total,
          realBookingFee
        };
      }
    )
  ),
  branch(
    props => props.trip.product_type !== 'trip',
    withProps(props => ({ total: props.total }))
  )
);

const PriceDetail = props => {
  const { isLoadingPricing, currency, rates, trip, total } = props;
  const rowRenderer = renderRow(isLoadingPricing);
  const convertPrice = priceConverter(currency, rates);
  const showInsurance = thaiCities.includes(trip.destination_location);
  return (
    <div>
      {trip.product_type === 'trip' && (() => {
        const { adultPerPerson, quantity, totalAdults, totalChild, child, bookingFee, bookNowAndSave, realBookingFee } = props;
        let adultLabel = quantity > 1 ? 'guests' : 'guest';
        let childLabel = '';
        if (child && child.price) {
          adultLabel = quantity > 1 ? 'adults' : 'adult';
          childLabel = child.quantity > 1 ? 'children' : 'child';
        }
        let feeTax = bookNowAndSave < 0 ? convertPrice(bookingFee) : convertPrice(realBookingFee);
        feeTax = myIsNaN(feeTax) ? 0 : feeTax;
        return (
          <Fragment>
            {rowRenderer(
              `${isLoadingPricing ? '' : `${currency} ${myIsNaN(convertPrice(adultPerPerson)) ? 0 : convertPrice(adultPerPerson)} x `}${quantity} ${adultLabel}`,
              `${currency} ${myIsNaN(convertPrice(totalAdults)) ? 0 : convertPrice(totalAdults)}`
            )}
            {totalChild > 0 && rowRenderer(
              `${currency} ${myIsNaN(convertPrice(child.price)) ? 0 : convertPrice(child.price)} x ${child.quantity} ${childLabel}`,
              `${currency} ${myIsNaN(convertPrice(totalChild)) ? 0 : convertPrice(totalChild)}`
            )}
            {rowRenderer(
              <TooltipWrapper><Text>Booking fee + tax</Text><StyledTooltip text={<span>This small fee helps us run the platform<br/>because we want to empower the locals<br />by not charging them to get listed here.</span>} /></TooltipWrapper>,
              `${currency} ${feeTax}`
            )}
            {bookNowAndSave < 0 && rowRenderer(
              'Book now & Save',
              `${currency} ${myIsNaN(convertPrice(bookNowAndSave)) ? 0 : convertPrice(bookNowAndSave)}`,
              false,
              true
            )}
            {showInsurance && rowRenderer(
              'Accident Insurance',
              <GreenText>FREE</GreenText>
            )}
          </Fragment>
        );
      })()}
      {rowRenderer(
        <MediumText>Total</MediumText>,
        <Total isMasterTrip={trip.is_master_trip}>{currency} {myIsNaN(convertPrice(total)) ? 0 : convertPrice(total)}</Total>,
        true
      )}
      <Satisfaction>100% Satisfaction guaranteed</Satisfaction>
    </div>
  );
};

PriceDetail.propTypes = {
  isLoadingPricing: PropTypes.bool,
  currency: PropTypes.string,
  rates: PropTypes.object,
  trip: PropTypes.object,
  total: PropTypes.number,
};

export default withTrip(enhance(PriceDetail));
